/**
 * CalendarControl — the Calendar Control prototype running in the rail
 * (2026-09-14, KP; rail port stages 1–2).
 *
 * One surface, four routes. The screen renders the prototype ITSELF, a patched
 * copy at app/public/calendar-control/control.dc.html, chromeless — layout,
 * brand and behaviour are the prototype's own, byte-faithful and deliberately
 * NOT the app design system. Same sanctioned exception, and the same embed
 * pipeline, as FloorCalendar's stage A (docs/design-baseline.md).
 *
 * Three seams only, all in the copy:
 *   ?chromeless=1  drops the board's brand bar; the app shell owns chrome.
 *   ?mode=         the entry mode, one per route.
 *   postMessage    the board's tab strip reports mode changes so the rail
 *                  selection and the URL follow; the host echoes the mode back
 *                  when the rail moves.
 *
 * Writes (2026-09-15, §3 step 2): every action on the board runs a real operation
 * through this host over a fifth seam — `calendar-control-op` in,
 * `calendar-control-op-result` back. The board owns no client and no token; it
 * name an operation from the allow-list in OPS below and gets the server's
 * answer or the server's error. The allow-list is the sixteen the outbox named,
 * plus startServingAdminBooking and listQueues (CD's corrections of 2026-09-15),
 * plus listEventGuests, bulkCheckInGuests and updateGuest (the dockets of * 2026-09-16 — the event roster is a READ and it was missing; the staff check-in
 * is bulkCheckInGuests, and its undo is updateGuest), plus the appointment EDIT
 * and the own cancel (kit 735b29f0, 2026-09-17: updateOwnAppointment /
 * updateAdminAppointment / cancelOwnAppointment / cancelAdminAppointment), plus
 * the queue console's own set and the two create-sheet reads (round 4,
 * 2026-09-17: callNextQueueEntry, recallQueueEntry, serveQueueEntry,
 * recordQueueEntryOutcome, leaveAdminQueueEntry, setStaffQueueAcceptance,
 * listEventTickets).
 * `documentOwnAppointment` and `documentAdminBooking` are DEPRECATED on the
 * platform and are no longer bound here at all — notes and outcome are fields of
 * the edit. An unknown callId is refused here, not guessed at.
 *
 * Three of them are barrel exports and are called by name. The rest are live
 * routes with no exported wrapper in this build (the wiring kit resolves them to
 * functions the package does not re-export, or to none at all), so they go through
 * the client's own `api()` helper with the path from the wiring kit and the callId
 * as the operation label — the same request the missing wrapper would make, with
 * the same auth, tenant routing and telemetry.
 *
 * Data (2026-09-15, kit 135d05dd): My Day and My Calendar are BOUND — this host
 * calls `getMyCalendar` (GET /v1/me/calendar) with the typed client and posts the
 * payload into the board, which renders both self modes from it. A fourth seam:
 *   calendar-control-range → the board asks for the window it is showing
 *   calendar-control-data / -data-error → the answer, or an honest failure
 * The read is session-scoped (`staffId` is REFUSED, not ignored), so the app
 * shell is the only caller that can make it: the board is a document in an
 * iframe with no access to the typed client. Floor and Schedule still render the
 * prototype's own data — this read does not cover another member's day by design,
 * and the rostered queue-duty block has no server object at all (CC → CD
 * 2026-09-15 §4 R1/R2).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  isApiError,
  api, getMyCalendar, checkInAdminAppointment, createAdminAppointment, listBookingCustomers,
  startServingAdminBooking, completeAdminBooking, markAdminBookingNoShow,
  checkInOwnAppointment, startServingOwnAppointment, completeOwnAppointment,
  markOwnAppointmentNoShow, updateOwnAppointment, cancelOwnAppointment,
  updateAdminAppointment, cancelAdminAppointment,
  admin,
} from "@v0y4ge/api-client";
import { Screen } from "../../shell/chrome";
import { useNav } from "../../shell/router";

export type CalendarMode = "myday" | "floor" | "schedule" | "mine";

/* Mode ↔ route ↔ rail item. The prototype holds the same table; these two must
 * agree, which is why both name the existing ids rather than inventing any. */
const ROUTE_OF: Record<CalendarMode, string> = {
  myday: "workforce.myday",
  floor: "workforce.floor",
  schedule: "workforce.schedule",
  mine: "workforce.myschedule",
};
const RAIL_OF: Record<CalendarMode, string> = {
  myday: "wf-myday",
  floor: "wf-floor",
  schedule: "wf-schedule",
  mine: "wf-mysched",
};

export default function CalendarControl({ mode }: { mode: CalendarMode }) {
  const nav = useNav();
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [ready, setReady] = useState(false);

  /* Public assets resolve through __STUDIO_ASSET_BASE__ (app/public), same
   * mechanism as FloorCalendar — a route-relative src would resolve against
   * /workforce/my-day and 404. Falls back to the Vite site-root convention.
   * Deliberately NOT keyed on `mode`: remounting the iframe on every mode
   * change would throw away the board's state and re-run its boot. The mode is
   * pushed in over postMessage instead. */
  const boardSrc = useMemo(() => {
    const base = (window as unknown as { __STUDIO_ASSET_BASE__?: string }).__STUDIO_ASSET_BASE__ ?? "";
    return (base ? base.replace(/\/$/, "") + "/" : "/") + "calendar-control/control.dc.html?chromeless=1&mode=" + mode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* The board's calendar read. One request per visible range, answered into the
   * frame; a stale answer is dropped on the board's side by requestId. `view`,
   * `date`, `from` and `to` are the board's — the query carries nothing that
   * selects WHOSE calendar this is, because the schema refuses it (422) and the
   * session is the only source of that. */
  const liveSeq = useRef(0);
  useEffect(() => {
    const post = (msg: Record<string, unknown>) => {
      const w = frameRef.current?.contentWindow;
      if (!w) return;
      try { w.postMessage(msg, window.location.origin); } catch { /* noop */ }
    };
    const onMsg = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      if (ev.source !== frameRef.current?.contentWindow) return;
      const m = ev.data as { type?: string; requestId?: number; view?: string; date?: string; from?: string; to?: string } | null;
      if (!m || m.type !== "calendar-control-range") return;
      const token = ++liveSeq.current;
      const q: { view?: "day" | "week" | "month" | "list"; date?: string; from?: string; to?: string } = {};
      if (m.view === "day" || m.view === "week" || m.view === "month" || m.view === "list") q.view = m.view;
      if (m.date) q.date = m.date;
      if (m.from) q.from = m.from;
      if (m.to) q.to = m.to;
      void getMyCalendar(q)
        .then((payload) => { if (token === liveSeq.current) post({ type: "calendar-control-data", requestId: m.requestId, payload }); })
        .catch((e: unknown) => {
          if (token !== liveSeq.current) return;
          post({ type: "calendar-control-data-error", requestId: m.requestId,
            message: e instanceof Error ? e.message : String(e) });
        });
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  /* The board's write seam. Each entry takes the argument bag the board sends and
   * returns the operation's promise; the board gets {ok, data} or {ok:false,
   * error}. Paths are the wiring kit's, verified live in backed-ops.json. */
  useEffect(() => {
    const enc = encodeURIComponent;
    const post = (path: string, body?: unknown, callId?: string) =>
      api(path, { method: "POST", body: JSON.stringify(body ?? {}) }, callId);
    type Args = Record<string, unknown>;
    const s = (a: Args, k: string) => String(a[k] ?? "");

    /* ── A LIFECYCLE WRITE MUST COME BACK AS A BOOKING ────────────────────────
     * `booking.ts` → `apiRaw` → `mapMutationResponse` neither throws nor reports
     * on a non-ok, non-JSON response: it RESOLVES `{}`. So `/complete`,
     * `/start-serving` and `/no-show` answered 501/404 and still fulfilled with an
     * empty object — no `error` for an absence-of-error check to catch, which is
     * why the first fix missed exactly these three. `checkInAdminAppointment`
     * (coded envelope) and every other op (they throw) were never affected.
     *
     * The guard is therefore POSITIVE: the server has to hand back the thing it
     * claims to have written — a booking or appointment carrying an `id`. Nothing
     * recognisable means the write is reported as failed, which is the honest
     * answer whether the cause was an unbound route, a 404 or a shape the mapper
     * could not read. */
    const entityOf = (value: unknown): { id?: unknown } | null => {
      if (!value || typeof value !== "object") return null;
      const v = value as Record<string, unknown>;
      for (const key of ["data", "booking", "appointment"]) {
        const nested = v[key];
        if (nested && typeof nested === "object" && (nested as { id?: unknown }).id) return nested as { id?: unknown };
      }
      return v.id ? v : null;
    };
    const mustReturnBooking = async (run: Promise<unknown>): Promise<unknown> => {
      const data = await run;
      if (entityOf(data)) return data;
      const envelope = (data && typeof data === "object" ? data : null) as { error?: { message?: unknown; code?: unknown } } | null;
      const named = envelope?.error;
      throw new Error(
        (named && String(named.message ?? named.code ?? "")) ||
        "the server returned no booking, so nothing was recorded",
      );
    };
    const OPS: Record<string, (a: Args) => Promise<unknown>> = {
      /* ── MY DAY: THE ADVISOR'S OWN APPOINTMENTS ─────────────────────────────
         Kit `kit-delta-2026-09-16-31a59e48` delivers the five own-appointment
         commands, so My Day no longer borrows the admin lane. These run on
         `/v1/me/appointments/{id}/…` and `appointments.edit_own`, verify
         signed-in account → canonical staff → booking assignment, and refuse a
         colleague's appointment. Their result is the canonical AdminAppointment,
         not the admin `{data, reversal}` envelope.

         THERE IS NO FALLBACK TO THE ADMIN CALLS. A permission failure is the
         answer, not a reason to try the privileged route — which is what would
         quietly hand an advisor a workflow the backend refuses them. The board
         picks its lane by mode; the admin ops below stay for the authorized
         privileged flows. The own check-in takes NO `intent` (that property is
         the positional admin contract's), so the board's admin-shaped input is
         deliberately not forwarded here. */
      checkInOwnAppointment: (a) => checkInOwnAppointment(s(a, "bookingId"), { source: "staff", notify: false } as never),
      startServingOwnAppointment: (a) => startServingOwnAppointment(s(a, "bookingId")),
      completeOwnAppointment: (a) => completeOwnAppointment(s(a, "bookingId"), (a.input as never) ?? ({ notify: false } as never)),
      markOwnAppointmentNoShow: (a) => markOwnAppointmentNoShow(s(a, "bookingId"), { notify: false, charge_late_fee: false } as never),

      /* ── THE APPOINTMENT EDIT, AND THE OWN CANCEL (kit 735b29f0) ───────────
         One PATCH per lane carries everything that is a plain FACT about the
         appointment: the venue-local window (reschedule), the note, the
         documented outcome, the three recorded instants and the contact
         channel. `null` on a nullable field clears it; an omitted key leaves it
         alone; unknown keys are refused (strict). The response is the full
         AdminAppointment, so the open sheet adopts what came back rather than
         predicting it — clearing `startedAt` also clears `completedAt` and the
         status is recomputed server-side.

         The documentation-only commands (`documentOwnAppointment` /
         `documentAdminBooking`) are DEPRECATED and deliberately no longer bound
         here: notes and outcome ride this edit. `outcome: 'noshow'` is refused
         by the schema — a no-show stays its own command. */
      updateOwnAppointment: (a) => updateOwnAppointment(s(a, "bookingId"), a.input as never),
      cancelOwnAppointment: (a) => cancelOwnAppointment(s(a, "bookingId"), (a.input as never) ?? ({} as never)),

      /* appointments — the PRIVILEGED lane, for an authorized admin flow only.
         `intent: 'check-in'` stays explicit here: the positional admin check-in
         advances whatever state the booking is in, so a retried call on an
         already-arrived booking can COMPLETE it. */
      checkInAdminAppointment: (a) =>
        checkInAdminAppointment(s(a, "appointmentId"), (a.input as never) ?? ({ intent: "check-in", source: "staff" } as never)),
      startServingAdminBooking: (a) => mustReturnBooking(startServingAdminBooking(s(a, "bookingId"))),
      completeAdminBooking: (a) => mustReturnBooking(completeAdminBooking(s(a, "bookingId"), (a.input as never) ?? ({} as never))),
      markAdminBookingNoShow: (a) => mustReturnBooking(markAdminBookingNoShow(s(a, "bookingId"), (a.input as never) ?? ({} as never))),
      /* The privileged twins of the two above. `cancelAdminAppointment` takes
         the reason positionally, not as a body. */
      updateAdminAppointment: (a) => updateAdminAppointment(s(a, "bookingId"), a.input as never),
      cancelAdminAppointment: (a) => {
        const input = (a.input ?? {}) as { reason?: unknown };
        const reason = input.reason == null ? undefined : String(input.reason);
        return cancelAdminAppointment(s(a, "bookingId"), reason as never);
      },
      createAdminAppointment: (a) => createAdminAppointment(a.input as never),
      /* queue */
      callQueueEntry: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/call`, a.input, "callQueueEntry"),
      completeQueueEntry: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/complete`, a.input, "completeQueueEntry"),
      markQueueEntryNoShow: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/no-show`, a.input, "markQueueEntryNoShow"),
      /* THE QUEUE-SCOPED MESSAGE ROUTE. `sendQueueMessage` on `/v1/staff/entries`
         is marked not-for-new-callers; `sendQueueEntryMessage` is the canonical
         one and needs the queue id as well as the entry's. Both stay bound, so an
         older board build does not lose its send. */
      sendQueueEntryMessage: (a) => post(`/v1/admin/queues/${enc(s(a, "queueId"))}/entries/${enc(s(a, "entryId"))}/messages`, a.input, "sendQueueEntryMessage"),
      sendQueueMessage: (a) => post(`/v1/staff/entries/${enc(s(a, "entryId"))}/message`, a.input, "sendQueueMessage"),
      setQueueStatus: (a) => post(`/v1/admin/queues/${enc(s(a, "queueId"))}/status`, a.input, "setQueueStatus"),
      addQueueEntry: (a) => post(`/v1/admin/queues/${enc(s(a, "queueId"))}/entries`, a.input, "addQueueEntry"),
      /* The add-to-queue picker's source: a label is not an id, and there is no
       * entry to read one off before the person is in the line. */
      listQueues: () => api("/v1/admin/queues", { method: "GET" }, "listQueues"),
      /* The Add-appointment service list. The board used to offer the seed's
       * service names per line, which in a self mode is Capture University's
       * catalogue baked into the component — a list that is wrong for every other
       * tenant and carries no service id to book with.
       *
       * THE PATH WAS WRONG (CD round 4, item 4): this called
       * `/v1/admin/services?limit=200`, which is not the contract's route —
       * `listStudioServices` is `GET /v1/admin/catalog/services` (`services.read`).
       * The "403 five times" in CD's trace was this seam asking for a route that
       * is not the operation's; the repeat came from the board re-asking after
       * each failure, which is fixed on its side too. */
      listStudioServices: () => api("/v1/admin/catalog/services?limit=200", { method: "GET" }, "listStudioServices"),
      listEventTickets: (a) => api(`/v1/admin/events/${enc(s(a, "eventId"))}/ticket-blocks`, { method: "GET" }, "listEventTickets"),
      /* THE QUEUE CONSOLE'S OWN OPERATIONS (CD round 4, item 1). `queue.serve`
         every one; the member persona holds it through `workforce.frontline_member`.
         `serveQueueEntry` starts the visit, `recordQueueEntryOutcome` writes what
         it was AFTER `completeQueueEntry` closes it, `leaveAdminQueueEntry` is
         "they left" as distinct from a no-show, and `setStaffQueueAcceptance` is
         whether the queue may hand you the next person — not a break. */
      callNextQueueEntry: (a) => post(`/v1/admin/queues/${enc(s(a, "queueId"))}/call-next`, a.input, "callNextQueueEntry"),
      recallQueueEntry: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/recall`, a.input, "recallQueueEntry"),
      serveQueueEntry: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/serve`, a.input, "serveQueueEntry"),
      recordQueueEntryOutcome: (a) => post(`/v1/staff/entries/${enc(s(a, "entryId"))}/outcome`, a.input, "recordQueueEntryOutcome"),
      leaveAdminQueueEntry: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/leave`, a.input, "leaveAdminQueueEntry"),
      /* "Return to line" DOES have an operation (CD's round-4 answers): a called
         or being-served entry goes back to waiting. The state machine refuses it
         on anything else, which is the board's guard too. */
      returnQueueEntryToQueue: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/return-to-queue`, a.input, "returnQueueEntryToQueue"),
      /* THE MEMBER'S OWN OVERRIDE LANE (kit eaf84fb6, `myschedule.write`). The
         admin lane below needs `settings.write`, a tenant right — so Block time on
         My Day / My Calendar uses these four and Floor / Schedule keep the admin
         ops. The server pins the staff from the session; a body naming one is
         refused, so none is sent. The client's export names are `listOwnOverrides`
         / `createOwnOverride` / `updateOwnOverride` / `deleteOwnOverride` — the
         callIds keep the contract's longer names, which is what the board says. */
      /* ── THE SCAN (CD scan docket, 2026-09-17) ──────────────────────────
         Bound on the docket's final routes ahead of the wiring kit, so the board's
         scanner exercises the real seam; until the platform PR is on the tenant
         these answer 404 and the scanner renders that refusal rather than faking a
         resolve. `entryRef` is the id the board reduced a manage URL to.
         `resolveOwnCalendarEntry` carries the effective policy for the entry — the
         board must not read manage settings, which a Team Member cannot. */
      resolveOwnCalendarEntry: (a) => api(`/v1/me/calendar/entries/${enc(s(a, "entryRef"))}`,
        { method: "GET" }, "resolveOwnCalendarEntry"),
      checkInOwnCalendarEntry: (a) => api(`/v1/me/calendar/entries/${enc(s(a, "entryRef"))}/check-in`,
        { method: "POST", body: JSON.stringify(a.input ?? { source: "qr" }) }, "checkInOwnCalendarEntry"),
      listOwnAvailabilityOverrides: (a) => admin.listOwnOverrides((a.query as never) ?? undefined),
      createOwnAvailabilityOverride: (a) => admin.createOwnOverride(a.input as never),
      updateOwnAvailabilityOverride: (a) => admin.updateOwnOverride(s(a, "overrideId"), a.input as never),
      deleteOwnAvailabilityOverride: (a) => admin.deleteOwnOverride(s(a, "overrideId")),
      setStaffQueueAcceptance: (a) => api("/v1/staff/queue-status", { method: "PUT", body: JSON.stringify(a.input ?? {}) }, "setStaffQueueAcceptance"),
      setStaffBreak: (a) => api("/v1/staff/break", { method: "PUT", body: JSON.stringify(a.input ?? {}) }, "setStaffBreak"),
      /* customers — TWO DIFFERENT READS, and the difference is the contract's
         (CD brief item 9). `/v1/customers/search` requires `field=email|phone`
         alongside `q` and cannot search a name; it used to be called with a bare
         name here, which returned nothing while the board reported a match.
         Name/text search is `listBookingCustomers`. A call with no valid field is
         refused here rather than sent as a query the route rejects. */
      listBookingCustomers: (a) => listBookingCustomers({ q: s(a, "q") } as never),
      searchCustomers: (a) => {
        const field = s(a, "field");
        if (field !== "email" && field !== "phone") {
          return Promise.reject(new Error("customers/search needs field=email or field=phone — a name is not searchable there; use listBookingCustomers"));
        }
        return api(`/v1/customers/search?field=${enc(field)}&q=${enc(s(a, "q"))}`, { method: "GET" }, "searchCustomers");
      },
      createCustomer: (a) => post("/v1/customers", a.input, "createCustomer"),
      /* own time — create/update/delete all typed now (the kit carries
         `admin.updateOverride` / `admin.deleteOverride`; their call ids are
         `updateAvailabilityOverride` / `deleteAvailabilityOverride`). These are
         ADMIN operations: a denial is surfaced, never worked around. */
      createAvailabilityOverride: (a) => post("/v1/admin/overrides", a.input, "createAvailabilityOverride"),
      updateAvailabilityOverride: (a) => admin.updateOverride(s(a, "overrideId"), a.input as never),
      deleteAvailabilityOverride: (a) => admin.deleteOverride(s(a, "overrideId")),
      /* events — THE ROSTER IS A READ, and it was missing (CD docket 2026-09-16
         item 5). The board generated the attendee list, so "Check in" had no
         registration to address and wrote locally. `eventGuests` is the canonical
         event-scoped guest list; one page of 200 covers every roster this sheet
         shows, and the board renders nothing when the read fails rather than
         inventing attendees. */
      /* `api()` UNWRAPS a `data` envelope, the typed exports do not — so this
         raw read hands back the guest ARRAY, not the body, and the board's
         `(res.guests || res.data) || []` read "0 of 0" off a 200 with two
         guests on it (CD 2026-09-17, 7a). Re-wrap to the body shape the board
         expects. `GuestListResponse.data` is a legacy alias of `guests`
         (JAI-1713); `guests` is the field to read. */
      listEventGuests: async (a) => {
        const r = await api(`/v1/admin/events/${enc(s(a, "eventId"))}/guests?limit=200`, { method: "GET" }, "eventGuests");
        return Array.isArray(r) ? { guests: r } : r;
      },
      /* The REGISTRANT's check-in is the platform's staff check-in
         `bulkCheckInGuests`, one guest id per tap (CD docket 2, 5b): it runs the
         whole flow server-side — status → checked_in, the `registration.checked_in`
         domain event for messaging, the attendance rollup, linked queue entries.
         `checkInRegistrationGuest` below is only the +1-guest variant of a
         registration, not the primary check-in.

         The path is the CONTRACT's, tenant-level with the event id in the body:
         `GUESTS_OPERATIONS.bulkCheckInGuests` is `/v1/admin/guests/bulk/check-in`
         and `eventId` is a field of `GuestBulkSelectionInput`. CD's docket named
         an event-scoped route and then confirmed (2026-09-16) that the contract
         path is canonical and the docket was wrong. */
      bulkCheckInGuests: (a) => post("/v1/admin/guests/bulk/check-in", a.input, "bulkCheckInGuests"),
      /* The registration's own fields, and the UNDO of a check-in: CD confirms
         `{ status: "registered" }` clears `checked_in_at` and that no separate
         undo command is coming. */
      updateGuest: (a) => api(`/v1/admin/guests/${enc(s(a, "guestId"))}`,
        { method: "PATCH", body: JSON.stringify(a.input ?? {}) }, "updateGuest"),
      addWalkupGuest: (a) => post(`/v1/admin/events/${enc(s(a, "eventId"))}/guests`, a.input, "addWalkupGuest"),
      checkInRegistrationGuest: (a) => post(
        `/v1/admin/events/${enc(s(a, "eventId"))}/registrations/${enc(s(a, "registrationId"))}/guests/${enc(s(a, "registrationGuestId"))}/check-in`,
        a.input, "checkInRegistrationGuest"),
    };
    const onMsg = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      const w = frameRef.current?.contentWindow;
      if (!w || ev.source !== w) return;
      const m = ev.data as { type?: string; requestId?: number; callId?: string; args?: Args } | null;
      if (!m || m.type !== "calendar-control-op") return;
      const reply = (body: Record<string, unknown>) => {
        try { w.postMessage({ type: "calendar-control-op-result", requestId: m.requestId, ...body }, window.location.origin); }
        catch { /* frame went away */ }
      };
      const run = m.callId ? OPS[m.callId] : undefined;
      if (!run) { reply({ ok: false, error: `${m.callId ?? "(none)"} is not a bound operation` }); return; }
      void Promise.resolve()
        .then(() => run(m.args ?? {}))
        .then((data) => {
          /* A FULFILLED PROMISE IS NOT A SUCCESSFUL WRITE.
           *
           * The admin booking wrappers (`booking.ts` → `apiRaw` →
           * `mapMutationResponse`) RESOLVE an error envelope instead of throwing,
           * so a 501 `ROUTE_UNBOUND` on `/complete` arrived here as a fulfilled
           * call: the board closed the sheet and flashed "complete" for a visit
           * the server never accepted. That is the one failure the delta brief
           * forbids outright, and no amount of board-side honesty can reach it —
           * the seam has to refuse first.
           *
           * So an envelope carrying an `error` is reported as a failure, with the
           * server's own code and message. Anything else passes through
           * untouched: a shape check would have to know 20 response contracts. */
          const envelope = (data && typeof data === "object" ? data : null) as { error?: unknown } | null;
          const err = envelope?.error;
          if (err) {
            const e = (typeof err === "object" ? err : { message: String(err) }) as { code?: unknown; message?: unknown; status?: unknown };
            reply({
              ok: false,
              error: String(e.message ?? "") || String(e.code ?? "") || "the server refused the write",
              code: e.code ? String(e.code) : undefined,
              status: typeof e.status === "number" ? e.status : undefined,
            });
            return;
          }
          reply({ ok: true, data: data ?? null });
        })
        .catch((e: unknown) => {
          /* `ApiError.message` is DELIBERATELY ALLOWED TO BE EMPTY (client.ts:
             "message is honest and may be ''; fallbackLabel is the always-present
             one"), and `instanceof` lies across realms — which is why the client
             exports `isApiError`. Reading `e.message` through `instanceof Error`
             therefore sent the board an empty string for every refusal, so a 403
             denial, a validation refusal and an unbound route all rendered as the
             same generic sentence. The code and status ride along so the board can
             tell a denial from a missing route. */
          if (isApiError(e)) {
            reply({ ok: false, error: e.fallbackLabel || e.message || e.code, code: e.code, status: e.status });
            return;
          }
          reply({ ok: false, error: e instanceof Error ? (e.message || String(e)) : String(e) });
        });
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  /* Rail → board. The route changed under us (rail click, deep link, back
   * button), so tell the board which mode it is now showing. */
  useEffect(() => {
    if (!ready) return;
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    try { w.postMessage({ type: "calendar-control-set-mode", mode }, window.location.origin); } catch { /* noop */ }
  }, [mode, ready]);

  /* Students → My Day: the record's Appointment / Event / Queue buttons arrive
   * as route params; the board opens that form with the student prefilled. */
  const createWhat = typeof nav.params.create === "string" ? nav.params.create : "";
  const createWho = typeof nav.params.who === "string" ? nav.params.who : "";
  useEffect(() => {
    if (!ready || !createWhat) return;
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    try { w.postMessage({ type: "calendar-control-create", what: createWhat, who: createWho }, window.location.origin); } catch { /* noop */ }
  }, [ready, createWhat, createWho]);

  /* THE DEEPLINK (scan docket B): `?day=YYYY-MM-DD&entry=<entryId>` lands My Day
   * (or My Calendar) on that day with the element's sheet open — the route a scan
   * navigates through, so a shared link and a scan behave the same. */
  const dlDay = typeof nav.params.day === "string" ? nav.params.day : "";
  const dlEntry = typeof nav.params.entry === "string" ? nav.params.entry : "";
  useEffect(() => {
    if (!ready || (!dlDay && !dlEntry)) return;
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    try { w.postMessage({ type: "calendar-control-entry", day: dlDay, entry: dlEntry }, window.location.origin); } catch { /* noop */ }
  }, [ready, dlDay, dlEntry]);

  /* THE RAIL'S SCAN (KP, 2026-09-17): the phone bottom rail's QR button routes
   * here with `?scan=<nonce>` from any screen; the nonce makes a second tap on
   * the same screen re-fire. The board opens its scanner. */
  const scanNonce = typeof nav.params.scan === "string" ? nav.params.scan : "";
  useEffect(() => {
    if (!ready || !scanNonce) return;
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    try { w.postMessage({ type: "calendar-control-scan" }, window.location.origin); } catch { /* noop */ }
  }, [ready, scanNonce]);

  /* Board → rail. */
  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      /* ORIGIN GATE — the board is served from our own origin, so this is an
       * exact match rather than an allowlist. Without it any window holding a
       * handle to Studio could drive the router. Same rule as FloorCalendar. */
      if (ev.origin !== window.location.origin) return;
      if (ev.source !== frameRef.current?.contentWindow) return;
      const m = ev.data as { type?: string; mode?: string } | null;
      if (!m || typeof m !== "object") return;
      if (m.type === "calendar-control-ready") { setReady(true); return; }
      if (m.type === "calendar-control-mode") {
        const next = m.mode as CalendarMode;
        if (!next || next === mode) return;
        const route = ROUTE_OF[next];
        if (route) nav.go(route);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [mode, nav]);

  return (
    <Screen rail={RAIL_OF[mode]}>
      <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <iframe
          ref={frameRef}
          title="Calendar"
          src={boardSrc}
          /* THE CAMERA CROSSES THE FRAME ONLY IF DELEGATED (KP, 2026-09-17): without
           * `allow="camera"` every getUserMedia inside the board is refused and the
           * scanner falls back to its "camera was not allowed" note — in production
           * too, not only in the preview. */
          allow="camera"
          style={{ flex: 1, minHeight: 0, width: "100%", border: "none", background: "#F4F5F7" }}
        />
      </div>
    </Screen>
  );
}
